import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  getAdminV1StoresStoreIdImports,
  getAdminV1StoresStoreIdImportsId,
  postAdminV1StoresStoreIdImports,
  type GetAdminV1StoresStoreIdImports200,
  type ImportJob,
  type ImportKind,
  type ImportOnExisting,
} from '@nucleus-commerce/api-client/admin';
import { getHttp } from '@nucleus-commerce/api-client/transport';

import { fallbackSampleFilename, filenameFromDisposition } from '../schemas/job-rules';

import { withEnvelope, type ExportFile } from './exports-api';

export interface SampleFile extends ExportFile {
  filename: string;
}

export const importsApi = {
  list: (storeId: string, params: ListParams): Promise<GetAdminV1StoresStoreIdImports200> =>
    getAdminV1StoresStoreIdImports({ storeId }, params),
  get: (storeId: string, id: string): Promise<ImportJob> =>
    getAdminV1StoresStoreIdImportsId({ storeId, id }),
  /**
   * The CSV goes as the raw request body, which the spec does not model, so it rides in the
   * request options. The Content-Type must be text/csv or the backend answers 415. The bytes are
   * read up front because not every axios adapter accepts a File (the Node one used in tests
   * does not); an ArrayBuffer goes through all of them unchanged.
   */
  create: async (
    storeId: string,
    kind: ImportKind,
    file: File,
    onExisting?: ImportOnExisting,
  ): Promise<ImportJob> =>
    postAdminV1StoresStoreIdImports({ storeId }, onExisting ? { kind, onExisting } : { kind }, {
      data: await file.arrayBuffer(),
      headers: { 'Content-Type': 'text/csv' },
    }),
  /** Read raw like an export download: the generated call drops the Content-Disposition header. */
  sample: async (storeId: string, kind: ImportKind): Promise<SampleFile> => {
    try {
      const response = await getHttp().get<string>(`/admin/v1/stores/${storeId}/imports/sample`, {
        params: { kind },
        responseType: 'text',
        headers: { Accept: 'text/csv, application/json' },
      });
      const disposition: unknown = response.headers['content-disposition'];
      return {
        filename:
          filenameFromDisposition(typeof disposition === 'string' ? disposition : null) ??
          fallbackSampleFilename(kind),
        blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      };
    } catch (error) {
      throw withEnvelope(error);
    }
  },
};
